import { useState } from 'react'
import { api } from '@carrent/api'
import {
  DAMAGE_TYPE_LABELS,
  DamageSchema,
  Icon,
  damageTypesFor,
  truckProjections,
  zoneLabel,
} from '@carrent/ui'

/**
 * Действия по итогу разбора поездки.
 *
 * Экран сравнения — место принятия решения. Диспетчер увидел
 * расхождение и здесь же его закрывает: спрашивает водителя,
 * заводит ремонт или фиксирует повреждение. Иначе после просмотра
 * всё равно надо звонить, и половина разборов теряется в телефоне.
 *
 * ── Повреждение пишется в паспорт, а не в акт водителя ────────────
 *
 * Кнопка называется «Записать в паспорт кузова», а не «Отметить
 * повреждение», и это не придирка к словам. Акт — документ,
 * зафиксированный водителем в конкретный момент; дописать в него
 * задним числом значит лишить его доказательной силы ровно в том
 * споре, ради которого сравнение и существует. Поэтому запись идёт
 * в паспорт — слой истины, которым администратор и так управляет
 * через подтверждение поездки, — со ссылкой на разбираемую поездку.
 * Формулировка кнопки говорит, что произойдёт на самом деле.
 */

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Не критично' },
  { value: 'critical', label: 'Критично — ехать нельзя' },
]

const TICKET_PRIORITIES = [
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]

function Sheet({ title, children, onClose }) {
  return (
    <div
      className="sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="sheet">
        <div className="sheet__header">
          <h3 className="sheet__title">{title}</h3>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Обращение к водителю по разбору поездки. */
function TicketForm({ trip, onDone, onMessage }) {
  const [subject, setSubject] = useState('Вопрос по поездке')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState('normal')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/ticket`, {
        subject: subject.trim(),
        body: body.trim(),
        category: 'technical',
        priority,
      })
      onMessage({ type: 'success', text: `Обращение №${data?.data?.id} открыто` })
      onDone()
    } catch (e) {
      onMessage({ type: 'error', text: e.response?.data?.message || 'Не удалось открыть обращение' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="action-form">
      <p className="action-form__note">
        Обращение появится у водителя, и он сможет ответить прямо в нём.
      </p>

      <div className="field">
        <label className="field__label" htmlFor="ticket-subject">Тема</label>
        <input id="ticket-subject" className="input" value={subject}
          onChange={(e) => setSubject(e.target.value)} />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="ticket-body">Что нужно выяснить</label>
        <textarea id="ticket-body" className="input action-form__text" rows={3}
          placeholder="Например: откуда царапина на левом борту?"
          value={body} onChange={(e) => setBody(e.target.value)} />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="ticket-priority">Приоритет</label>
        <select id="ticket-priority" className="input" value={priority}
          onChange={(e) => setPriority(e.target.value)}>
          {TICKET_PRIORITIES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      <button type="button" className="btn btn--primary btn--full"
        onClick={submit} disabled={busy || !subject.trim() || !body.trim()}>
        {busy ? <span className="spinner" /> : <Icon name="chat" size={16} />}
        Открыть обращение
      </button>
    </div>
  )
}

/** Запись повреждения в паспорт кузова. */
function DamageForm({ trip, onDone, onMessage }) {
  const [projection, setProjection] = useState('front')
  const [spot, setSpot] = useState(null)
  const [damageType, setDamageType] = useState(null)
  const [severity, setSeverity] = useState('minor')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)

  const projections = truckProjections(trip.car?.body_type,
    { hasTailLift: trip.car?.has_tail_lift })

  if (!projections) {
    return (
      <p className="action-form__note">
        Для этого типа кузова схема повреждений не ведётся.
      </p>
    )
  }

  const pickZone = (zone, point) => {
    setSpot({ zone, point })
    // Список дефектов зависит от материала зоны: для тента бессмысленна
    // вмятина, для стекла — порез. Сбрасываем выбор при смене зоны.
    setDamageType(null)
  }

  const types = spot ? damageTypesFor(spot.zone.material) : []

  const submit = async () => {
    setBusy(true)
    try {
      await api.post(`/admin/cars/${trip.car.id}/damages`, {
        zone_id: spot.zone.id,
        x: spot.point.x,
        y: spot.point.y,
        damage_type: damageType,
        severity,
        comment: comment.trim() || undefined,
        origin_trip_id: trip.id,
      })
      onMessage({ type: 'success', text: 'Повреждение записано в паспорт кузова' })
      onDone()
    } catch (e) {
      onMessage({ type: 'error', text: e.response?.data?.message || 'Не удалось записать' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="action-form">
      <p className="action-form__note">
        Запись попадёт в паспорт кузова со ссылкой на эту поездку. Акт водителя
        не меняется — он остаётся тем, что было зафиксировано при сдаче.
      </p>

      <div className="damage-diff__tabs" role="tablist" aria-label="Проекции кузова">
        {projections.map((p) => (
          <button key={p.key} type="button" role="tab"
            aria-selected={p.key === projection}
            className={`damage-diff__tab${p.key === projection ? ' is-active' : ''}`}
            onClick={() => setProjection(p.key)}>
            {p.label}
          </button>
        ))}
      </div>

      <DamageSchema
        projections={projections}
        activeKey={projection}
        marks={spot ? [{
          client_mark_id: 'draft', zone_id: spot.zone.id,
          x: spot.point.x, y: spot.point.y, status: 'new',
        }] : []}
        onZoneTap={pickZone}
      />

      {!spot && (
        <p className="action-form__hint">
          Укажите место на схеме — по нему повреждение найдётся на следующей приёмке.
        </p>
      )}

      {spot && (
        <>
          <p className="action-form__zone">{zoneLabel(spot.zone.id)}</p>

          <div className="field">
            <label className="field__label" htmlFor="damage-type">Что повреждено</label>
            <select id="damage-type" className="input" value={damageType || ''}
              onChange={(e) => setDamageType(e.target.value || null)}>
              <option value="">Выберите тип</option>
              {types.map((t) => (
                <option key={t} value={t}>{DAMAGE_TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="damage-severity">Критичность</label>
            <select id="damage-severity" className="input" value={severity}
              onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="damage-comment">Комментарий</label>
            <input id="damage-comment" className="input" maxLength={300}
              placeholder="Необязательно"
              value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>

          <button type="button" className="btn btn--primary btn--full"
            onClick={submit} disabled={busy || !damageType}>
            {busy ? <span className="spinner" /> : <Icon name="plus" size={16} />}
            Записать в паспорт кузова
          </button>
        </>
      )}
    </div>
  )
}

export default function TripActions({ trip, onMessage, onChanged }) {
  const [open, setOpen] = useState(null)

  const close = () => setOpen(null)
  const done = () => { close(); onChanged?.() }

  return (
    <>
      <button type="button" className="btn btn--secondary btn--full"
        onClick={() => setOpen('ticket')}>
        <Icon name="chat" size={16} />
        Спросить водителя
      </button>

      {trip.car?.id && (
        <button type="button" className="btn btn--secondary btn--full"
          onClick={() => setOpen('damage')}>
          <Icon name="warning" size={16} />
          Записать повреждение
        </button>
      )}

      {open === 'ticket' && (
        <Sheet title="Обращение к водителю" onClose={close}>
          <TicketForm trip={trip} onDone={done} onMessage={onMessage} />
        </Sheet>
      )}

      {open === 'damage' && (
        <Sheet title="Повреждение в паспорт кузова" onClose={close}>
          <DamageForm trip={trip} onDone={done} onMessage={onMessage} />
        </Sheet>
      )}
    </>
  )
}
